import { publicationStateForSave } from '../../utils/formPublication';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { DonationFlag } from 'donation-elements';
import { getAuthToken, getAuthGroups } from '../../utils/auth';
import { toast } from 'sonner';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import ElementEditorSection, {
  elementEditorSectionHasErrors,
  elementEditorSectionIcons,
  focusFirstInvalidElement,
} from '../workspaces/ElementEditorSection';
import '../../css/multi-select-styling.css';

// Import tab components
import FundraisingFlagConfiguration from './tabs/FundraisingFlagConfiguration';
import AppearanceTab from './tabs/AppearanceTab';

import type { FormData, ColorPickerColor } from './tabs/types';
import type {
  CreateFundraisingFlagDrawerProps,
  FundraisingFlagFormData,
  Tab,
  WorkspaceHeaderAction,
} from './types/FundraisingFlagTypes';
import { portalClient } from '../../lib/trpc/portal';

// Utility function for classNames
function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

const tabs: Tab[] = [
  { name: 'Configuration', step: 'configuration', current: true },
  { name: 'Appearance', step: 'appearance', current: false },
];

const initialFormData: FundraisingFlagFormData = {
  elementName: '',
  orientation: 'RIGHT',
  allowedDomains: [],
  disallowedDomains: [],
  color: '',
  formIDs: [],
  connectedForms: [],
  salesforceCampaignID: '',
};

export default function CreateFundraisingFlagDrawer(props: CreateFundraisingFlagDrawerProps) {
  const paymentsReady = useOnboardingDriver()?.chargesEnabled === true;
  const useCreateWorkspaceCards = props.presentation === 'workspace';
  const [formData, setFormData] = useState<FundraisingFlagFormData>(initialFormData);
  const [elementID, setElementID] = useState<string | null>(null);
  const [availableForms, setAvailableForms] = useState<FormData[]>([]);
  const [active, setActive] = useState<boolean>(true);

  // Add validation states
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState<string>('configuration');

  // Add loading states
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isActivating, setIsActivating] = useState<boolean>(false);

  // Color picker state
  const [sketchPickerColor, setSketchPickerColor] = useState<ColorPickerColor>({
    r: 0,
    g: 0,
    b: 0,
    a: 1,
  });
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);

  // Add new state for storing domain suggestions from other elements
  const [existingDomainSuggestions, setExistingDomainSuggestions] = useState<{
    whitelist: string[];
    blacklist: string[];
  }>({
    whitelist: [],
    blacklist: [],
  });

  // Filter out CharityStack internal URLs
  const isCharityStackInternalURL = (domain: string): boolean => {
    const normalizedDomain = domain.toLowerCase().trim();
    if (/(?:^https?:\/\/)?(?:www\.)?charitystack\.com\//i.test(normalizedDomain)) {
      return true;
    }
    return false;
  };

  // Add form reference
  const formRef = useRef<HTMLFormElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  // Update formData helper
  const updateFormData = (field: keyof FundraisingFlagFormData, value: any): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle field focus to clear validation errors
  const handleFieldFocus = (fieldName: string) => {
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      newInvalidFields.delete(fieldName);
      const nonPrefixedName = fieldName.includes('-')
        ? fieldName.split('-').slice(1).join('-')
        : fieldName;
      newInvalidFields.delete(nonPrefixedName);
      return newInvalidFields;
    });
  };

  // Function to check if a field is invalid
  const isFieldInvalid = (fieldName: string) => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      if (invalidFields.has(fieldName)) {
        return true;
      }
      for (const tab of tabs) {
        if (invalidFields.has(`${tab.step}-${fieldName}`)) {
          return true;
        }
      }
      return false;
    }
    return false;
  };

  // Fetch domain suggestions from existing elements
  useEffect(() => {
    const fetchExistingDomains = async (): Promise<void> => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements?type=whitelist_and_blacklist`,
          {
            method: 'GET',
            headers: {
              Authorization: await getAuthToken(),
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) throw new Error('Failed to fetch domain suggestions');

        const data = await response.json();

        const allWhitelist = new Set<string>();
        const allBlacklist = new Set<string>();

        (data.whitelist || []).forEach((domain: string) => {
          if (!isCharityStackInternalURL(domain)) {
            allWhitelist.add(domain);
          }
        });

        (data.blacklist || []).forEach((domain: string) => {
          if (!isCharityStackInternalURL(domain)) {
            allBlacklist.add(domain);
          }
        });

        const filteredWhitelist = Array.from(allWhitelist);
        const filteredBlacklist = Array.from(allBlacklist);

        setExistingDomainSuggestions({
          whitelist: filteredWhitelist,
          blacklist: filteredBlacklist,
        });
      } catch (error) {
        console.error('Error fetching domain suggestions:', error);
      }
    };

    fetchExistingDomains();
  }, []);

  // Handle form submission
  const handleSubmit = async (): Promise<void> => {
    const newInvalidFields = new Set<string>();

    if (!formData.elementName.trim()) {
      newInvalidFields.add('configuration-elementName');
    }
    if (!formData.connectedForms || formData.connectedForms.length === 0) {
      newInvalidFields.add('configuration-connectedForms');
    }
    if (!formData.allowedDomains || formData.allowedDomains.length === 0) {
      newInvalidFields.add('configuration-allowedDomains');
    }
    if (!formData.color || !formData.color.trim()) {
      newInvalidFields.add('appearance-color');
    }
    if (!formData.orientation) {
      newInvalidFields.add('appearance-orientation');
    }

    if (newInvalidFields.size > 0) {
      setInvalidFields(newInvalidFields);
      focusFirstInvalidElement(rightPanelRef.current);
      toast.error('Please fix validation errors');
      return;
    }

    setIsSaving(true);

    try {
      // Build formIDs from connected forms
      const formIDs = formData.connectedForms
        .map((formName) => availableForms.find((form) => form.formName === formName)?.formID)
        .filter((id): id is string => id !== undefined);

      const payload: any = {
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        CSMerchantID: props.merchant?.organizationId || '',
        whitelist: formData.allowedDomains || [],
        blacklist: formData.disallowedDomains || [],
        color: formData.color,
        elementName: formData.elementName,
        elementType: 'DONATION_FLAG',
        orientation: formData.orientation,
        destinationType: 'POPUP',
        formNames: formData.connectedForms || [],
        formIDs: formIDs,
        salesforceCampaignID: formData.salesforceCampaignID || '',
        active: publicationStateForSave({ existingId: elementID, active, paymentsReady }),
        ...(!elementID && {
          timeCreated: new Date().toISOString(),
          createdBy: props.user?.email || 'unknown',
        }),
      };

      if (elementID) {
        payload.elementID = elementID;
        delete payload.active;
      }

      const method = elementID ? 'PATCH' : 'POST';
      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          toast.error(data.error);
        } catch {
          throw new Error('API request failed');
        }
        return;
      }

      const data = await response.json();

      toast.success(
        elementID
          ? 'Element updated successfully'
          : paymentsReady
            ? 'Element published successfully'
            : 'Draft saved successfully',
      );
      if (!elementID) {
        setActive(data.active ?? payload.active);
        setElementID(data.elementID);
      }
      if (props.onSuccess) props.onSuccess(data);
    } catch (error: any) {
      console.error('Submission error:', error);
      toast.error(`Failed to ${elementID ? 'update' : 'publish'} element`, {
        description: error?.message || 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Activate/Deactivate element
  const activateElement = async (): Promise<void> => {
    setIsActivating(true);
    const state = !active;
    if (state && !paymentsReady) {
      setIsActivating(false);
      toast.info('Enable payments before publishing this draft.');
      return;
    }
    const successAlertMessage = state ? 'Element Activated' : 'Element Deactivated';

    try {
      const payload = {
        elementID,
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        active: state,
      };

      const requestOptions = {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      };

      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements/toggle-active`;
      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        return;
      } else {
        toast.success(successAlertMessage);
        setActive(state);
        if (props.onSuccess) props.onSuccess(data);
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error('Failed to update element status');
    } finally {
      setIsActivating(false);
    }
  };

  // Fetch forms
  useEffect(() => {
    const fetchForms = async (): Promise<void> => {
      if (!props.open) return;

      try {
        const merchants = await getAuthGroups();

        const response = await portalClient.postFormAndFundData({
          authorization: await getAuthToken(),
          body: {
            queryStringParameters: {
              nonprofit: merchants[0],
            },
          },
        });

        const data = await response.json();
        setAvailableForms(data.formNames);
      } catch (error) {
        console.error('Error fetching forms:', error);
        setAvailableForms([]);
      }
    };

    fetchForms();
  }, [props.open]);

  // Populate form data for edit mode
  useEffect(() => {
    if (props.elementData && props.open) {
      const data = props.elementData;

      // Handle legacy data: convert domainURL to whitelist array
      let allowedDomains: string[] = [];
      if (data.whitelist && Array.isArray(data.whitelist)) {
        // Modern format with whitelist array
        allowedDomains = data.whitelist.filter((domain) => !isCharityStackInternalURL(domain));
      } else if (data.domainURL) {
        // Legacy format with single domainURL - add it to the beginning of the array
        allowedDomains = [data.domainURL];
      }

      // Handle legacy data: convert single formID/formName to arrays
      let formIDs: string[] = [];
      let connectedForms: string[] = [];

      if (data.formIDs && Array.isArray(data.formIDs)) {
        // Modern format with arrays
        formIDs = data.formIDs;
      } else if (data.formID) {
        // Legacy format with single formID
        formIDs = [data.formID];
      }

      if (data.formNames && Array.isArray(data.formNames)) {
        // Modern format with arrays
        connectedForms = data.formNames;
      } else if (data.formName) {
        // Legacy format with single formName
        connectedForms = [data.formName];
      }

      setElementID(data.elementID);
      setFormData({
        elementName: data.elementName,
        orientation: data.orientation || 'RIGHT',
        allowedDomains: allowedDomains,
        disallowedDomains: data.blacklist || [],
        color: data.color,
        formIDs: formIDs,
        connectedForms: connectedForms,
        salesforceCampaignID: data.salesforceCampaignID || '',
      });
      setActive(data.active);
      setCurrentStep('configuration');
    }
  }, [props.elementData, props.open]);

  // Pre-fill the element color from the org's brand color when the drawer opens.
  // Only applies when (a) the drawer is open, (b) the org has a brand color set, and
  // (c) the color field is still untouched (empty) — so a color the user has picked
  // or one loaded from an existing element in edit mode is never overwritten.
  useEffect(() => {
    if (!props.open) return;
    const orgBrandColor = props.merchant?.brandColor;
    if (!orgBrandColor) return;
    setFormData((prev) => {
      if (prev.color) return prev; // user picked one, or edit-mode loaded one
      return { ...prev, color: orgBrandColor };
    });
  }, [props.open, props.merchant?.brandColor]);

  const clearStates = (): void => {
    setFormData(initialFormData);
    setElementID(null);
    setAvailableForms([]);
    setInvalidFields(new Set());
    setCurrentStep('configuration');
    setSketchPickerColor({ r: 0, g: 0, b: 0, a: 1 });
    setDisplayColorPicker(false);
    setActive(true);
    setIsSaving(false);
    setIsActivating(false);

    if (formRef.current) formRef.current.reset();
  };

  const handleSubmitLatest = useLatestCallback(handleSubmit);
  const activateElementLatest = useLatestCallback(activateElement);

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (elementID) {
      actions.push(
        {
          key: 'save',
          label: isSaving ? 'Saving...' : 'Save',
          variant: 'primary' as const,
          disabled: isSaving || isActivating,
          onClick: () => handleSubmitLatest(),
        },
        {
          key: 'active',
          label: !active
            ? isActivating
              ? 'Activating...'
              : 'Activate'
            : isActivating
              ? 'Deactivating...'
              : 'Deactivate',
          disabled: isActivating || isSaving || (!active && !paymentsReady),
          variant: !active ? 'primary' : 'danger',
          onClick: () => activateElementLatest(),
        },
      );
    } else {
      actions.push({
        key: 'publish',
        label: isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft',
        disabled: isSaving || isActivating,
        variant: 'primary' as const,
        onClick: () => handleSubmitLatest(),
      });
    }

    props.setWorkspaceActions(actions);
    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    isSaving,
    isActivating,
    elementID,
    active,
    paymentsReady,
    handleSubmitLatest,
    activateElementLatest,
  ]);

  // Don't render if required props are missing
  if (!props.merchant) {
    return null;
  }

  const effectiveColor = formData.color || '#9A9285'; // Default warm muted color

  // Position classes based on orientation using absolute positioning
  const getPositionClasses = () => {
    switch (formData.orientation) {
      case 'TOP':
        return 'absolute top-0 left-1/2 -translate-x-1/2 -top-9';
      case 'BOTTOM':
        return 'absolute bottom-0 left-1/2 -translate-x-1/2 -bottom-9';
      case 'LEFT':
        return 'absolute -left-9 top-1/2 rotate-90 -translate-y-1/2';
      case 'RIGHT':
      default:
        return 'absolute -right-9 top-1/2 -rotate-90 -translate-y-1/2';
    }
  };

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        props.setOpen(open);
        if (!open) clearStates();
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        className={
          props.presentation === 'workspace'
            ? 'min-h-full overflow-visible rounded-none bg-white p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
        onEscapeKeyDown={() => clearStates()}
        onPointerDownOutside={() => clearStates()}
      >
        {/* Main Container - centered */}
        <div
          className={`mx-auto w-full max-w-[1600px] ${
            props.presentation === 'workspace' ? 'px-3 py-2 md:py-3' : 'h-full pointer-events-none'
          }`}
        >
          {/* Header with Action Buttons */}
          {props.presentation !== 'workspace' && (
            <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
              <div className="flex items-start gap-x-[6px]">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-6 w-6"
                  >
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6h-5.6z" fill="#6B6458" />
                  </svg>
                </div>
                <div>
                  <p className="text-base text-ink-muted">
                    {elementID ? 'Edit Fundraising Flag' : 'Create Fundraising Flag'}
                  </p>
                  <p className="text-sm text-ink-muted">
                    Add a floating donation prompt to your website
                  </p>
                </div>
              </div>

              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    props.setOpen(false);
                    clearStates();
                  }}
                >
                  Close
                </Button>

                {/* Save Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={isSaving || isActivating}
                    variant="primary"
                    size="workspace"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}

                {/* Activate/Deactivate Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => activateElement()}
                    disabled={isActivating || isSaving || (!active && !paymentsReady)}
                    variant={!active ? 'primary' : 'danger'}
                    size="workspace"
                  >
                    {!active
                      ? isActivating
                        ? 'Activating...'
                        : 'Activate'
                      : isActivating
                        ? 'Deactivating...'
                        : 'Deactivate'}
                  </Button>
                )}

                {/* Publish Button - Only when creating */}
                {!elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    variant="primary"
                    size="workspace"
                    disabled={isSaving || isActivating}
                  >
                    {isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {props.presentation !== 'workspace' && (
            <div className="pb-3">
              <Separator className="bg-paper-inset" />
            </div>
          )}

          {/* Main Content - Split Layout */}
          <div
            className={`pointer-events-auto flex w-full flex-col-reverse gap-6 lg:flex-row ${
              props.presentation === 'workspace'
                ? 'overflow-visible'
                : 'h-[calc(100%-80px)] overflow-y-auto md:overflow-hidden'
            }`}
          >
            {/* Left Side - Preview Section */}
            <div
              className={`flex min-h-[200px] max-h-[200px] flex-col gap-4 pr-2 md:max-h-[250px] lg:w-1/2 ${
                useCreateWorkspaceCards
                  ? 'overflow-visible'
                  : 'h-full overflow-y-auto md:min-h-full'
              }`}
            >
              {/* CON-42: this frame is dashboard chrome; its contents are a donor
                  component, which would otherwise resolve every --cs-* against
                  dashboard.css. See decisions.md D-previewer-scope. */}
              <div
                data-cs-position="donor"
                className="flex w-full flex-col items-stretch rounded-md border border-edge bg-paper-muted overflow-hidden"
              >
                <div className="relative h-[200px] w-full">
                  <div className={getPositionClasses()}>
                    <DonationFlag
                      previewMode={true}
                      color={effectiveColor}
                      orientation={formData.orientation}
                      destinationType="POPUP"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Form Configuration */}
            <div
              ref={rightPanelRef}
              className={`compact-editor-controls flex flex-col lg:w-1/2 lg:pl-4 ${
                useCreateWorkspaceCards
                  ? 'min-h-0 overflow-visible pb-0'
                  : 'min-h-[320px] overflow-hidden pb-14 md:min-h-full md:pb-0'
              }`}
            >
              {/* Form Tabs */}
              {!useCreateWorkspaceCards && (
                <div className="border-edge bg-white">
                  <nav
                    className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                    aria-label="Tabs"
                  >
                    {tabs.map((tab) => {
                      const tabHasErrors = Array.from(invalidFields).some((field) =>
                        field.startsWith(tab.step + '-'),
                      );
                      return (
                        <button
                          type="button"
                          key={tab.name}
                          onClick={() => setCurrentStep(tab.step)}
                          className={classNames(
                            currentStep === tab.step
                              ? tabHasErrors
                                ? 'border-red-500 text-red-600'
                                : 'border-ink-secondary text-ink-secondary'
                              : tabHasErrors
                                ? 'border-transparent text-red-500 hover:text-red-600'
                                : 'border-transparent text-ink-muted hover:text-ink-secondary',
                            'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                          )}
                        >
                          {tab.name}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              )}

              {/* Form Content */}
              <div
                className={`flex-1 ${
                  useCreateWorkspaceCards ? 'space-y-4 overflow-visible' : 'overflow-y-auto py-4'
                }`}
              >
                {/* Configuration Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'configuration'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-2'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Configuration"
                    description="Choose the form and domains where this fundraising flag can appear."
                    icon={elementEditorSectionIcons.configuration}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'configuration')}
                  >
                    <FundraisingFlagConfiguration
                      formData={formData}
                      updateFormData={updateFormData}
                      availableForms={availableForms}
                      existingDomainSuggestions={existingDomainSuggestions}
                      isCharityStackInternalURL={isCharityStackInternalURL}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                    />
                  </ElementEditorSection>
                </div>

                {/* Appearance Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'appearance'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-4'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Appearance"
                    description="Set the color and screen position of the fundraising flag."
                    icon={elementEditorSectionIcons.appearance}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'appearance')}
                  >
                    <AppearanceTab
                      color={formData.color}
                      setColor={(value) => updateFormData('color', value)}
                      sketchPickerColor={sketchPickerColor}
                      setSketchPickerColor={setSketchPickerColor}
                      displayColorPicker={displayColorPicker}
                      setDisplayColorPicker={setDisplayColorPicker}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                      elementType="fundraising-flag"
                      orientation={formData.orientation}
                      setOrientation={(value) => updateFormData('orientation', value)}
                    />
                  </ElementEditorSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
